import type { FormEvent, ReactInstance } from 'react';

import domtoimage from 'dom-to-image';
import JSZip from 'jszip';
import _ from 'lodash';
import {
  exportComponentAsJPEG,
  exportComponentAsPDF,
} from 'react-component-export-image';

export const useExport = (input: {
  patientName: string;
  page: number;
  componentRef: React.RefObject<ReactInstance>;
  isPrintMode?: boolean;
}) => {
  const { patientName, componentRef, isPrintMode } = input;

  const handleExport = async (
    e: FormEvent<HTMLButtonElement>,
    type: string,
  ) => {
    e.preventDefault();
    const name = `${_.kebabCase(patientName)}-findings-summary`;

    if (isPrintMode) {
      // Get all page divs
      const pageDivs = (componentRef.current as HTMLElement)?.querySelectorAll(
        '.vai-bg-white.vai-border',
      );
      if (!pageDivs?.length) return;

      // Create zip file
      const zip = new JSZip();

      // Convert each page to JPEG and add to zip
      for (let i = 0; i < pageDivs.length; i++) {
        const element = pageDivs[i] as HTMLElement;

        // Convert to PNG first to preserve quality
        const dataUrl = await domtoimage.toPng(element, {
          quality: 1.0,
          bgcolor: '#ffffff',
          style: {
            transform: 'none',
            'transform-origin': 'top left',
            'font-family': 'proxima-nova, sans-serif',
            'font-size': '16px',
            'line-height': '1.5',
            'letter-spacing': '0.01em',
            'word-spacing': '0.05em',
          },
          filter: (node) => {
            // Apply specific styling to footer text
            if (
              node instanceof HTMLElement &&
              node.classList.contains("vai-font-['proxima-nova']")
            ) {
              node.style.fontFamily = 'proxima-nova, sans-serif';
              node.style.fontSize = '14px';
              node.style.lineHeight = '1';
              node.style.margin = '0';
            }
            return true;
          },
        });

        // Convert data URL to blob
        const response = await fetch(dataUrl);
        const blob = await response.blob();

        // Add to zip
        zip.file(`${name}-page-${i + 1}.jpeg`, blob);
      }

      // Download zip file
      const content = await zip.generateAsync({ type: 'blob' });
      const url = window.URL.createObjectURL(content);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${name}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } else if (type === 'pdf') {
      exportComponentAsPDF(componentRef, {
        fileName: name,
        pdfOptions: {
          w: 440,
          h: 620,
          unit: 'px',
        },
      });
    } else {
      exportComponentAsJPEG(componentRef, {
        fileName: name,
      });
    }
  };

  return { handleExport };
};
